<template>
  <div
    class="edit-qualification bg-background"
    :class="{ invisible: isCameraActive }"
  >
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="$router.back()" />
      </template>
      資格編集
    </Header>

    <RejectionReason
      v-if="isRejected"
      :details="rejectionReasonDetails"
      :comment="rejectionReasonComment"
    />

    <div class="py-4 mx-3">
      <div v-if="isUnverified || isVerified" class="caution-area mb-4">
        <div class="d-flex align-items-center caution">
          <FeatherIcon icon="info" size="24" />
          <div class="ml-1">
            <div
              v-if="isUnverified"
              class="caution"
              data-testid="unverified-label"
            >
              この資格は審査中です
            </div>
            <div v-if="isVerified" class="caution" data-testid="verified-label">
              この資格は認証済みです
            </div>
          </div>
        </div>
        <div v-if="verifiedComment" class="mt-2" data-testid="verified-comment">
          認証時のコメント: {{ verifiedComment }}
        </div>
      </div>

      <CustomValidationObserver
        ref="vObserver"
        v-slot="{ handleSubmit }"
        :initialValues="formValues"
      >
        <EditQualificationForm
          v-model="inputValues"
          v-model:isCameraActive="isCameraActive"
          :qualificationOptions="filteredQualificationOptions"
          :verifiedQualification="verifiedQualification"
          :disabledQualificationId="!isRejected || verifiedQualification"
          :disabledImage="isUnverified"
          :disabledText="disabledText"
        />
        <AppButton
          block
          :disabled="!canReverify"
          class="mt-4"
          @click="handleSubmit(onClickSubmit)"
        >
          再認証を申請する
        </AppButton>
        <AppButtonOutline
          v-if="!isVerified"
          block
          variant="red"
          class="mt-3"
          :disabled="isSubmitting"
          @click="onClickDelete"
        >
          {{ deleteQualificationLabel }}
        </AppButtonOutline>
      </CustomValidationObserver>
    </div>
  </div>
</template>

<script lang="ts">
import { h } from "vue";
import { Component, Vue } from "vue-facing-decorator";
import Header from "@/components/organisms/common/Header.vue";
import AppButton from "@/components/atoms/button/AppButton.vue";
import AppButtonOutline from "@/components/atoms/button/AppButtonOutline.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import RejectionReason from "@/components/atoms/other/RejectionReason.vue";
import EditQualificationForm from "@/components/organisms/mypage/EditQualificationForm.vue";
import { accountService } from "@/app/services/AccountService";
import VerifiedStatus from "@/models/verifiedStatus";
import CustomValidationObserver from "@/plugins/CustomValidationObserver.vue";
import { showErrorModal, ErrorType } from "@/utils/showErrorModal";
import { UserQualification } from "@/types/qualification/UserQualification";
import { UserQualificationInputValues } from "@/types/qualification/EditableUserQualificationItems";
import { Qualification } from "@/types/Master";
import { authStore } from "@/store/auth";

@Component({
  components: {
    Header,
    AppButton,
    AppButtonOutline,
    FeatherIcon,
    RejectionReason,
    EditQualificationForm
  }
})
export default class MypageEditQualification extends Vue {
  inputValues: UserQualificationInputValues = {
    image: null,
    qualification: null
  };
  qualifications: UserQualification[] = [];
  qualification: UserQualification | null = null;
  verifiedQualification: UserQualification | null = null;
  isCameraActive = false;
  isSubmitting = false;

  get formValues() {
    return {
      image: this.inputValues.image,
      qualification: this.inputValues.qualification
    };
  }

  get qualificationId() {
    return Number(this.$route.params.qualificationId);
  }

  get initialUploadImage() {
    return this.qualification?.image;
  }

  get isUnverified() {
    return this.qualification?.status === VerifiedStatus.UNVERIFIED;
  }

  get isVerified() {
    return this.qualification?.status === VerifiedStatus.VERIFIED;
  }

  get isRejected() {
    return this.qualification?.status === VerifiedStatus.REJECTED;
  }

  get rejectionReasonDetails() {
    return this.qualification?.rejectionReason?.details;
  }

  get rejectionReasonComment() {
    return this.qualification?.comment;
  }

  get verifiedComment() {
    return this.isVerified ? this.qualification?.comment : "";
  }

  get disabledText() {
    return "審査中のため変更できません。";
  }

  get isUpdatedImage() {
    return this.inputValues.image !== this.initialUploadImage;
  }

  get isUnderReexaminationAfterVerified() {
    if (!this.qualification) {
      return true;
    }
    return this.qualification.isUnderReexaminationAfterVerified;
  }

  get isEditableVerifiedQualification() {
    return this.isVerified && !this.isUnderReexaminationAfterVerified;
  }

  get canReverify() {
    return (
      (this.isRejected || this.isEditableVerifiedQualification) &&
      this.isUpdatedImage &&
      !this.isSubmitting
    );
  }

  get hasVerifiedQualification() {
    return !!this.verifiedQualification;
  }

  get deleteQualificationLabel() {
    return this.hasVerifiedQualification
      ? "申請を取り下げる"
      : "この資格を削除する";
  }

  async created() {
    try {
      this.$loading.show();
      await Promise.all([
        this.fetchQualifications(),
        this.fetchQualification()
      ]);
      this.initInput();
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  mounted() {
    // 取得した値でフォームをリセットしておく
    // https://github.com/logaretm/vee-validate/issues/4602
    (this.$refs.vObserver as InstanceType<
      typeof CustomValidationObserver
    >).resetForm(this.formValues);
  }

  async fetchQualifications() {
    const { results } = await accountService.fetchQualifications();
    this.qualifications = results;
  }

  async fetchQualification() {
    const qualification = await accountService.fetchQualification(
      this.qualificationId
    );
    this.qualification = qualification;
    if (qualification.verifiedQualificationId) {
      this.verifiedQualification = await accountService.fetchQualification(
        qualification.verifiedQualificationId
      );
    }
  }

  get filteredQualificationOptions(): Qualification[] {
    // 既に登録済みの資格は省く（現在選択中の項目以外）
    return authStore.product.qualifications.filter(r =>
      this.qualifications
        .filter(q => q.qualification !== this.qualification?.qualification)
        .every(q => q.qualification !== r.id)
    );
  }

  initInput() {
    this.inputValues.image = this.qualification?.image ?? null;
    this.inputValues.qualification = this.qualification?.qualification ?? null;
  }

  async onClickSubmit() {
    if (this.isSubmitting) return;

    // アップロード写真が変更されていない場合は、更新しない（undefinedを送信）
    const uploadImage = !this.isUpdatedImage
      ? undefined
      : this.inputValues.image;

    this.isSubmitting = true;

    try {
      this.$loading.show();

      if (this.isVerified) {
        // 認証済みの状態では画像しか変更できないため、このケースは原則起こらないはず
        if (!this.isUpdatedImage) {
          this.$bvModal.msgBoxOk("画像が選択されていません", {
            okVariant: "warning",
            centered: true
          });
          return;
        }

        const isOk = await this.$bvModal.msgBoxConfirm(
          [
            h(
              "p",
              "認証されるまでの期間中、申請中の資格で応募できなくなります"
            ),
            h("p", "よろしいですか？")
          ],
          {
            bodyClass: "text-primary-dark",
            okTitle: "理解しました",
            cancelTitle: "キャンセル",
            cancelVariant: "gray-03",
            centered: true
          }
        );
        if (!isOk) {
          return;
        }

        await accountService.updateVerifiedQualificationImage(
          this.qualificationId,
          uploadImage!
        );
      } else {
        await accountService.patchQualifications(
          this.qualificationId,
          uploadImage,
          this.inputValues.qualification!
        );
      }

      await this.$bvModal.msgBoxOk("資格の再認証を申請しました", {
        okVariant: "success",
        centered: true
      });
      this.$router.back();
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.isSubmitting = false;
      this.$loading.hide();
    }
  }

  async onClickDelete() {
    if (this.isSubmitting) return;

    if (!this.hasVerifiedQualification) {
      const isOk = await this.$bvModal.msgBoxConfirm("資格を削除しますか？", {
        bodyClass: "text-primary-dark",
        okTitle: "削除する",
        cancelTitle: "キャンセル",
        cancelVariant: "gray-03",
        centered: true
      });
      if (!isOk) {
        return;
      }
    }

    this.isSubmitting = true;

    try {
      this.$loading.show();
      await accountService.deleteQualifications(this.qualificationId);
      await this.$bvModal.msgBoxOk(
        this.verifiedQualification
          ? "申請を取り下げました"
          : "資格を削除しました。",
        {
          okVariant: "success",
          centered: true
        }
      );
      this.$router.back();
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.isSubmitting = false;
      this.$loading.hide();
    }
  }
}
</script>

<style lang="scss" scoped>
.edit-qualification {
  position: relative;
  min-height: inherit;
}

.caution-area {
  padding: 16px;
  font-size: 12px;
  background-color: $primary-bg;
  border: 1px solid $yellow;
  border-radius: 2px;

  .caution {
    font-size: 14px;
    font-weight: 500;
    color: $orange-70;
  }
}
</style>
